import { observer } from 'mobx-react-lite';
import { useTranslation } from 'next-i18next';
import styles from '@features/cart/components/CartProductQuantity/CartProductQuantity.styles';
import theme from '@styles/theme';
import { AvailabilityType, CartProductAvailabilityType } from '@features/cart/typescript';
import Icon from '@components/icons/Icon';
import QuantityInput from '@components/QuantityInput/QuantityInput';
import useInflectedTranslation from '@hooks/useInflectedTranslation';
type PropsType = {
  position?: string;
  quantity: number;
  availability: CartProductAvailabilityType;
  isProductLoading: boolean;
  showAvailabilityDate: boolean;
  allowChangeQuantity: boolean;
  miniCart: boolean;
  date: number | null;
  showProductInfo: boolean;
  classNames?: Record<string, unknown>;
  onChangeQty: (qty: number) => void;
  confirmUnavailableQuantity?: (position: string | null) => void;
  isDip: boolean;
  multiplication: string;
  alreadyConfirmedUnavailabilityQuantity?: boolean;
  alertReducedQuantity?: boolean;
};
const CartProductQuantity = ({
  position,
  quantity,
  availability,
  isProductLoading,
  showAvailabilityDate,
  allowChangeQuantity,
  miniCart,
  classNames = {},
  date,
  showProductInfo,
  onChangeQty,
  confirmUnavailableQuantity,
  isDip,
  multiplication,
  alreadyConfirmedUnavailabilityQuantity,
  alertReducedQuantity
}: PropsType) => {
  const {
    t
  } = useTranslation('common');
  const {
    getInflectedTranslation
  } = useInflectedTranslation();
  const warnIconColor = theme.rawColors.accent700;
  const timeIconColor = theme.rawColors.darkGrey500;
  const {
    amount,
    status
  } = availability;
  const productUnavailable = status === AvailabilityType.unavailable;
  const productLimited = status === AvailabilityType.notEnough && amount > 0 && amount < quantity;
  const productAvailabilityDate = !!(showAvailabilityDate && date && status === AvailabilityType.available && amount === 0) && <div sx={styles.availabilityWarningContainer(showProductInfo)} data-cy="CartProductDeliveryWarning">
      <div sx={styles.deliveryWarningIcon}>
        <Icon icon="WatchLater" addSx={styles.timeIcon} color={timeIconColor} size="22px" />
      </div>
      <div sx={styles.deliveryWarning}>
        <div sx={styles.deliveryWarning}>{t('cart.deliveryAt', {
          days: date
        })}</div>
      </div>
    </div>;
  const additionalInfo = productUnavailable || productLimited || productAvailabilityDate || alreadyConfirmedUnavailabilityQuantity || alertReducedQuantity;
  return <div sx={classNames ? {
    ...classNames,
    ...styles.quantityContainer
  } : styles.quantityContainer} data-cy="CartProductQuantity">
      <div sx={styles.changeQuantityButtonContainer}>
        {allowChangeQuantity ? <QuantityInput position={position} qty={quantity || 1} error={alreadyConfirmedUnavailabilityQuantity || alertReducedQuantity} handleChangeQty={onChangeQty} confirmUnavailableQuantity={confirmUnavailableQuantity} disabled={isProductLoading || productUnavailable || productLimited} isDip={isDip} multiplication={multiplication} maxValue={productLimited ? availability.amount : 999} /> : <div sx={styles.quantity} data-cy="CartProductQuantityValue">
            {isDip ? `${quantity} ${getInflectedTranslation(quantity || 1, 'pack')}` : t('cart.quantityPluralSimple', {
          quantity
        })}
          </div>}
      </div>
      {additionalInfo && <div sx={styles.warningWrapper(miniCart)}>
          {productUnavailable && <div sx={styles.availabilityWarningContainer(showProductInfo)} data-cy="CartProductQuantityUnavailable">
              <Icon icon="ErrorOutline" size="20px" color={warnIconColor} />
              <div sx={styles.availabilityWarning(!miniCart)}>{t('product.unavailable')}</div>
            </div>}
          {productLimited && showProductInfo && !alreadyConfirmedUnavailabilityQuantity && <div sx={styles.availabilityWarningContainer(showProductInfo)} data-cy="CartProductQuantityLimited">
              <Icon icon="ErrorOutline" size="20px" color={warnIconColor} />
              <div sx={styles.availabilityWarning(!miniCart)}>{t('product.quantityLimited')}</div>
            </div>}
          {(alertReducedQuantity || alreadyConfirmedUnavailabilityQuantity) && <div sx={styles.availabilityWarningContainer(showProductInfo)} data-cy="CartProductQuantityLimited">
              <Icon icon="ErrorOutline" size="20px" color={warnIconColor} />
              <div sx={styles.availabilityWarning(!miniCart)}>{t('alerts.quantityChanged')}</div>
            </div>}
          {productAvailabilityDate}
        </div>}
    </div>;
};
export default observer(CartProductQuantity);